import React, { useEffect, useMemo } from "react";
import { useViewerStore } from "../../../../../../store/store";
import {
    useProductionPanelLoading,
    useProductionPanelLoadingMessage,
} from "../../../../../../hooks/calculationStateHook";
import { ModuleMesh, RoofMesh } from "../../../../../../@types/types";
import { getRoofModuleMeshes, GetRoundNum } from "../../../Three-JS-Viewer/Controllers/Common";
import { RefreshModuleLabels } from "../../../Three-JS-Viewer/Controllers/ModuleControl";
import ProductionLoader from "./ProductionLoader";
import { useCurrentUser } from "../../../../../../contexts/UserContext";

interface ModuleRow {
    moduleId: string;
    label: string;
    production: number;
    multiplier: number;
    solarAccess: number;
}

interface RoofModuleGroup {
    roofFaceId: string;
    roofNum: number | string;
    buildingName: number | string;
    modules: ModuleRow[];
}

const ModulesInformationTable: React.FC = () => {
    const isPending = useProductionPanelLoading();
    const loadingMessage = useProductionPanelLoadingMessage();
    const { hasCapability } = useCurrentUser();
    const canShowModuleMultipliers = hasCapability('Modules_Multipliers');
    const sceneViewer = useViewerStore(state => state.sceneViewer);
    const roofInfoArr = useViewerStore(state => state.roofInfoArr);
    const accessoryBuildingList = useViewerStore(state => state.accessoryBuildingList);

    // Refresh labels when loading starts (spinner) or finishes (multipliers)
    useEffect(() => {
        if (!sceneViewer) return;
        if (!useViewerStore.getState().isModuleMultiplierLabelVisible) return;
        RefreshModuleLabels(sceneViewer);
    }, [isPending, sceneViewer]);

    const roofModuleGroups = useMemo((): RoofModuleGroup[] => {
        if (!sceneViewer?.roofMeshArr) return [];

        const accessoryRoofFaceIds = new Set<string>();
        for (const roof of sceneViewer.roofMeshArr as RoofMesh[]) {
            if (accessoryBuildingList.includes(roof.userData.buildingName)) {
                accessoryRoofFaceIds.add(roof.roofFaceId);
            }
        }

        return (sceneViewer.roofMeshArr as RoofMesh[])
            .filter((roof: RoofMesh) => !accessoryRoofFaceIds.has(roof.roofFaceId))
            .map((roof: RoofMesh) => {
                const roofNum = roof.userData.nameOverride ?? roof.userData.roof_tag_id;
                const modules = getRoofModuleMeshes(roof).map((module: ModuleMesh, idx: number) => {
                    const moduleNum = module.moduleNum ?? idx + 1;
                    return {
                        moduleId: module.moduleId,
                        label: module.moduleLabel ?? `R${roofNum}M${moduleNum}`,
                        production: module.production ?? 0,
                        multiplier: module.multiplier ?? 0,
                        solarAccess: (module.efficiency ?? 0) * 100,
                    };
                });

                return {
                    roofFaceId: roof.roofFaceId,
                    roofNum,
                    buildingName: roof.userData.buildingName,
                    modules,
                };
            })
            .filter(group => group.modules.length > 0);
    }, [sceneViewer, roofInfoArr, accessoryBuildingList, isPending]);

    return (
        <div className={"tab-content-container"}>
            <div className={"tab-content-scrollable-container"}>
                {isPending ? (
                    <ProductionLoader isPopoutVersion={true} message={loadingMessage} />
                ) : roofModuleGroups.length === 0 ? (
                    <div style={{ padding: 12 }}>No modules placed.</div>
                ) : (
                    roofModuleGroups.map((group) => (
                        <div key={group.roofFaceId} style={{ marginBottom: 16 }}>
                            <div className="tab__column--subtitle" style={{ margin: "8px 10px" }}>
                                Roof {group.roofNum}
                                {group.buildingName != null ? ` - Building ${group.buildingName}` : ""}
                            </div>
                            <table className={"tab__table"}>
                                <thead>
                                    <tr>
                                        <th>Label</th>
                                        <th>Production</th>
                                        <th>Solar Access</th>
                                        {canShowModuleMultipliers && <th>Multiplier</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.modules.map((row) => (
                                        <tr key={row.moduleId}>
                                            <td align="center">{row.label}</td>
                                            <td align="center">{GetRoundNum(row.production, 1)}</td>
                                            <td align="center">{GetRoundNum(row.solarAccess, 1)}%</td>
                                            {canShowModuleMultipliers && (
                                                <td align="center">{GetRoundNum(row.multiplier, 4)}</td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ModulesInformationTable;
